import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Sparkles, ChevronRight, Trash2, Loader2 } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { deleteDream } from '../lib/api';
import { toast } from 'sonner';

const MOOD_COLORS = {
  peaceful: 'text-blue-400',
  anxious: 'text-amber-400',
  confused: 'text-violet-400',
  frightened: 'text-red-400',
  excited: 'text-emerald-400',
  sad: 'text-gray-400',
  neutral: 'text-gray-400',
};

export function DreamCard({ dream, onDelete }) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  const truncateContent = (content, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };
  
  const handleDelete = async (e) => {
    e.stopPropagation(); // Prevent navigating to dream detail
    setIsDeleting(true);
    try {
      await deleteDream(dream.dream_id);
      toast.success('Dream deleted');
      if (onDelete) {
        onDelete(dream.dream_id);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete dream');
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <div 
      className="dream-card glass-card p-4 cursor-pointer group"
      onClick={() => navigate(`/dream/${dream.dream_id}`)}
      data-testid={`dream-card-${dream.dream_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-muted-foreground">
              {formatDate(dream.created_at)}
            </span>
            {dream.analysis && (
              <span className="flex items-center gap-1 text-xs text-primary">
                <Sparkles className="w-3 h-3" />
                Analyzed
              </span>
            )}
          </div>
          <h3 className="font-serif font-bold text-lg leading-tight">{dream.title}</h3>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Delete Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={(e) => e.stopPropagation()}
                data-testid={`delete-dream-card-${dream.dream_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass-card border-border" onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this dream?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete "{dream.title}" and its analysis.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
        {truncateContent(dream.content)}
      </p>
      
      <div className="flex items-center gap-3">
        {dream.mood && (
          <span className={`text-xs capitalize ${MOOD_COLORS[dream.mood] || 'text-gray-400'}`}>
            {dream.mood}
          </span>
        )}
        
        {dream.symbols?.length > 0 && (
          <div className="flex gap-1">
            {dream.symbols.slice(0, 3).map((symbol, index) => (
              <span 
                key={index}
                className="px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground"
              >
                {symbol}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DreamCard;
